export interface FpsBreakpoint {
  passmark: number;
  '1080p': Record<string, number>;
  '1440p': Record<string, number>;
  '4k': Record<string, number>;
}

export interface GameBenchmark {
  breakpoints: FpsBreakpoint[];
}

export type BenchmarkData = Record<string, GameBenchmark>;

/**
 * Linearly interpolates an FPS value from benchmark breakpoints
 * based on a product's combined passmark score.
 *
 * - Below lowest breakpoint: clamps to lowest FPS
 * - Above highest breakpoint: clamps to highest FPS
 * - Between breakpoints: linear interpolation
 */
export function interpolateFps(
  benchmarkData: BenchmarkData,
  gameSlug: string,
  passmarkScore: number,
  resolution: string,
  graphics: string,
): number {
  const game = benchmarkData[gameSlug];
  if (!game?.breakpoints?.length) return 0;

  const bp = game.breakpoints;

  const getFps = (breakpoint: FpsBreakpoint): number => {
    const resData = breakpoint[resolution as keyof FpsBreakpoint];
    if (typeof resData === 'object' && resData !== null) {
      return (resData as Record<string, number>)[graphics] ?? 0;
    }
    return 0;
  };

  // Clamp below lowest breakpoint
  if (passmarkScore <= bp[0].passmark) {
    return getFps(bp[0]);
  }

  // Clamp above highest breakpoint
  if (passmarkScore >= bp[bp.length - 1].passmark) {
    return getFps(bp[bp.length - 1]);
  }

  // Find surrounding breakpoints and interpolate
  for (let i = 0; i < bp.length - 1; i++) {
    const lo = bp[i];
    const hi = bp[i + 1];
    if (passmarkScore >= lo.passmark && passmarkScore <= hi.passmark) {
      const t = (passmarkScore - lo.passmark) / (hi.passmark - lo.passmark);
      const fpsLo = getFps(lo);
      const fpsHi = getFps(hi);
      return Math.round(fpsLo + t * (fpsHi - fpsLo));
    }
  }

  return 0;
}
